import {
  Scene,
  Vector3 as BVector3,
  DirectionalLight,
  HemisphericLight,
  ShadowGenerator,
  Color3,
  Color4,
  type Engine,
  type AbstractMesh
} from '@babylonjs/core';
import { TerrainGrid } from '../world/terrain-grid.js';

export class SceneManager {
  public readonly scene: Scene;
  public readonly terrain: TerrainGrid;
  public readonly sunLight: DirectionalLight;
  public readonly ambientLight: HemisphericLight;
  public readonly shadowGenerator: ShadowGenerator;

  constructor(engine: Engine) {
    // High-DPI scaling capped at 2.0 to protect GPU fill-rate on Retina displays
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
    engine.setHardwareScalingLevel(1 / Math.min(dpr, 2.0));

    this.scene = new Scene(engine);
    this.scene.clearColor = new Color4(0.05, 0.07, 0.12, 1.0);

    // Directional sunlight casting shadows
    const sunDir = new BVector3(-0.5, -1.0, 0.4).normalize();
    this.sunLight = new DirectionalLight('sunLight', sunDir, this.scene);
    this.sunLight.position = new BVector3(25, 45, -20);
    this.sunLight.intensity = 1.25;
    this.sunLight.diffuse = new Color3(1.0, 0.96, 0.88);

    this.shadowGenerator = new ShadowGenerator(1024, this.sunLight);
    this.shadowGenerator.useBlurExponentialShadowMap = true;
    this.shadowGenerator.blurKernel = 16;
    this.shadowGenerator.darkness = 0.4;

    // Ambient fill light
    this.ambientLight = new HemisphericLight('ambientLight', new BVector3(0, 1, 0), this.scene);
    this.ambientLight.intensity = 0.4;
    this.ambientLight.diffuse = new Color3(0.55, 0.7, 0.85);
    this.ambientLight.groundColor = new Color3(0.12, 0.16, 0.2);

    // Procedural terrain grid ground
    this.terrain = new TerrainGrid({ size: 60, subdivisions: 30 });
    this.terrain.createMesh(this.scene);
  }

  public registerShadowCaster(mesh: AbstractMesh): void {
    this.shadowGenerator.addShadowCaster(mesh);
  }

  public dispose(): void {
    this.shadowGenerator.dispose();
    this.sunLight.dispose();
    this.ambientLight.dispose();
    this.scene.dispose();
  }
}
